import { ApolloClient, gql } from '@apollo/client';

import {
  GetThirdJsonDocument,
  GetThirdJsonQuery,
  GetThirdJsonQueryVariables
} from '../../generated/graphql';
import { isUnknownFieldError } from '../../utils/graphqlFieldSkew';

/**
 * `getThirdJson` exactly as it was before `transactionType` was added to
 * `GetThirdJsonResponse`.
 *
 * A backend that predates that field rejects the current document outright:
 * an unknown field is a *validation* error, so the whole operation is refused
 * and no `json` comes back at all — Phase 3 then has no survey to render and
 * sits on Loading forever. That is not hypothetical version skew. The
 * development environment deploys foxcom-forms-backend from `master` while
 * this frontend branch (and the backend change it depends on) live on the
 * release branch, so every Phase 3 load in dev — including the Playwright
 * e2e-coverage run, which drives a locally served build against the dev
 * backend — hits exactly that mismatch until the release lands on `master`.
 *
 * Keeping the pre-field document lets the fetch below fall back to it, so the
 * survey still renders and only the transaction type is missing. The fallback
 * costs one refused round trip per Phase 3 load, and only while a backend
 * without the field is answering — once it has the field, the first attempt
 * succeeds and this document is never sent.
 */
export const GetThirdJsonWithoutTransactionTypeDocument = gql`
  query getThirdJsonWithoutTransactionType(
    $applicationId: ObjectID!
    $thirdJsonFileName: String!
  ) {
    getThirdJson(
      applicationId: $applicationId
      thirdJsonFileName: $thirdJsonFileName
    ) {
      json
      previousAnswers
      answersFromSecondJson
      savedAnswers
    }
  }
`;

type ThirdJsonResponse = GetThirdJsonQuery['getThirdJson'];

/**
 * True when the failure is the server refusing `transactionType` as an unknown
 * field. The detection itself is shared with every other document that has to
 * survive the same version skew — see utils/graphqlFieldSkew.
 */
const isUnknownTransactionTypeField = (error: unknown): boolean =>
  isUnknownFieldError(error, ['transactionType']);

/**
 * Fetches the Phase 3 survey JSON, tolerating a backend that does not yet
 * expose `getThirdJson.transactionType`. On that one failure the query is
 * re-issued without the field and `transactionType` comes back null, which the
 * caller already treats as "unknown" — every other failure propagates.
 */
const fetchThirdJson = async (
  client: ApolloClient<unknown>,
  variables: GetThirdJsonQueryVariables
): Promise<ThirdJsonResponse> => {
  try {
    const { data } = await client.query<
      GetThirdJsonQuery,
      GetThirdJsonQueryVariables
    >({
      query: GetThirdJsonDocument,
      variables,
      fetchPolicy: 'network-only'
    });
    return data?.getThirdJson ?? null;
  } catch (error) {
    if (!isUnknownTransactionTypeField(error)) throw error;

    const { data } = await client.query<
      GetThirdJsonQuery,
      GetThirdJsonQueryVariables
    >({
      query: GetThirdJsonWithoutTransactionTypeDocument,
      variables,
      fetchPolicy: 'network-only'
    });
    return data?.getThirdJson ?? null;
  }
};

export default fetchThirdJson;
